<template>
  <section v-bind:id="id" class="section">
    <h2>
      <i class="material-icons left">{{ iconName }}</i>
      {{ title }}
    </h2>

    <InfoCard v-bind="infoCard">
      <p>
        Para realizar a busca por usuários, primeiramente busque pelo servidor
        responsável através de seu CPF ou nome. Uma lista suspensa será exibida
        com os resultados possíveis. Em seguida, selecione o servidor
        responsável para que seus usuários associados sejam listados, onde é
        possível verificar as informações.
      </p>
    </InfoCard>

    <div class="row">
      <BaseTypeahead
        v-bind="servidorResponsavelTypeahead"
        v-bind:value="formattedServidorResponsavel"
        v-on:change="onServidorResponsavelChange"
        v-on:keydown.enter.self="onKeydown"
      />
    </div>

    <BaseProgressBar v-show="showProgressBar" v-bind="progressBar" />

    <div v-show="!loading && hasUsuarios" class="usuarios-list">
      <BaseEditableCollection v-bind="usuarioCollection" />
      <BasePagination
        class="center"
        v-bind="pagination"
        v-on:change="onPageChange"
      />
    </div>

    <UsuarioModal v-bind="userModal" />

    <UsuarioCreationConfirmModal
      v-bind="usuarioCreationConfirmModal"
      v-on:confirm="createUser"
    />

    <ChangePasswordModal v-bind="changePasswordModal" />
  </section>
</template>

<script>
import { API_URL } from "../../constants";
import BaseBuscaUsuarioItem from "./BaseBuscaUsuarioItem.vue";
import ChangePasswordModal from "../ChangePasswordModal/ChangePasswordModal.vue";
import UsuarioCreationConfirmModal from "./UsuarioCreationConfirmModal.vue";
import UsuarioModal from "./UsuarioModal.vue";

export default {
  name: "Usuarios",
  components: {
    ChangePasswordModal,
    UsuarioCreationConfirmModal,
    UsuarioModal
  },
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: true
    },

    title: String,
    iconName: String,

    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      creatingUsuario: false,
      pageSize: 10,
      infoCard: {
        id: "sispessoal-controleacesso-usuarios-infocard",
        title: "Busca de usuários"
      },
      changePasswordModal: {
        id: "sispessoal-controleacesso-usuarios-trocarsenha-modal",
        value: null
      },
      usuarioCreationConfirmModal: {
        id: "sispessoal-controleacesso-usuarios-creation-confirmmodal",
        value: null
      },
      userModal: {
        id: "sispessoal-controleacesso-usuarios-modal",
        value: null
      },
      pagination: {
        value: 1,
        numPages: 0
      },
      progressBar: {
        content: null,
        useContainer: false
      },
      showProgressBar: false,
      servidorResponsavel: null,
      servidorResponsavelTypeahead: {
        id: "sispessoal-controleacesso-usuarios-responsavel-typeahead",
        name: "responsavel",
        label: "Servidor Responsável",
        placeholder: "Busque pelo nome ou CPF do servidor responsável",
        dropdownId: "sispessoal-controleacesso-usuarios-responsavel-dropdown",
        searchFunction: this.searchServidorResponsavel,
        gridExpression: "col s12"
      },
      usuarioCollection: {
        component: BaseBuscaUsuarioItem,
        items: null,
        itemOperations: [
          {
            iconName: "edit",
            color: "green",
            disabled: this.disabled,
            onClick: this.openUsuarioModal,
            title: "Editar as permissões do usuário"
          },
          {
            iconName: "vpn_key",
            color: "blue",
            disabled: this.disabled,
            onClick: this.openChangePasswordModal,
            title:
              "Configurar a senha do usuário, que pode ser usada como alternativa ao login via intranet"
          }
        ]
      }
    };
  },
  computed: {
    formattedServidorResponsavel() {
      return this.mapServidorResponsavelToDropdownItem(
        this.servidorResponsavel
      );
    },
    hasUsuarios() {
      return (this.usuarioCollection.items || []).length;
    }
  },
  methods: {
    openChangePasswordModal(item, index) {
      const usuarios = this.usuarioCollection.items || [];

      if (index >= 0 && index < usuarios.length) {
        this.changePasswordModal.value = { userId: usuarios[index].value.id };
        this.$nextTick().then(() =>
          this.$utils.Components.openModal(this.changePasswordModal.id)
        );
      }
    },
    openUsuarioCreationConfirmModal() {
      if (this.servidorResponsavel) {
        this.usuarioCreationConfirmModal.value = this.servidorResponsavel;
        this.$nextTick().then(() =>
          this.$utils.Components.openModal(this.usuarioCreationConfirmModal.id)
        );
      }
    },
    openUsuarioModal(item, index) {
      const usuarios = this.usuarioCollection.items || [];

      if (index >= 0 && index < usuarios.length) {
        this.userModal.value = { ...usuarios[index].value };
        this.$nextTick().then(() =>
          this.$utils.Components.openModal(this.userModal.id)
        );
      }
    },
    getPageUsuarios(page) {
      if (!this.servidorResponsavel) {
        this.$notification.pushError("Servidor responsável não definido");
        return;
      }

      this.progressBar.content = "Carregando usuários...";
      this.showProgressBar = this.loading = true;

      const cpfResponsavel = this.servidorResponsavel.cpf;

      this.$http
        .get(
          `${API_URL}/acesso/usuario?cpfResponsavel=${cpfResponsavel}&size=${this.pageSize}&page=${page}`
        )
        .then(response => {
          if ((response.data.elementos || []).length === 0) {
            this.resetBuscaUsuarios();
            this.openUsuarioCreationConfirmModal();
            return;
          }

          this.pagination.numPages = Math.ceil(
            response.data.tamanhoTotal / this.pageSize
          );

          this.usuarioCollection.items = (response.data.elementos || []).map(
            item => ({
              value: {
                ...item,
                // Adiciona as informações do servidor responsável previamente buscado pelo typeahead
                servidorResponsavel: { ...this.servidorResponsavel }
              }
            })
          );
        })
        .catch(error =>
          this.$notification.pushError(
            `Houve um erro ao obter a lista de usuários: ${error.message}`
          )
        )
        .finally(() => (this.showProgressBar = this.loading = false));
    },
    async createUser(event) {
      if (!window.M || !event) {
        return;
      }

      const cpfResponsavel = (this.servidorResponsavel || {}).cpf;

      // TODO: implementar validador e colocar no módulo Utils
      if ((cpfResponsavel || "").length !== 11) {
        return this.$notification.pushError(
          "Dados do servidor responsável inválidos"
        );
      }

      let url = `${API_URL}/acesso/usuario`;

      const body = {
        cpfResponsavel,
        nome: cpfResponsavel,
        tipo: "PessoaFisica"
      };

      this.progressBar.content = "Criando usuário para o servidor...";
      this.showProgressBar = this.creatingUsuario = true;

      try {
        await this.$http.post(url, body);

        this.$notification.pushSuccess("Usuário criado com sucesso");

        // Após a criação do usuário, obtém a lista de usuários do servidor responsável
        this.getPageUsuarios(0);
      } catch (error) {
        this.$notification.pushError(
          `Houve um erro ao criar usuário: ${error.message}`
        );
      } finally {
        this.showProgressBar = this.creatingUsuario = false;
      }
    },
    mapServidorResponsavelToDropdownItem(item) {
      if (!item) {
        return null;
      }

      return {
        id: item.id,
        content: `${item.cpf} - ${item.nome}`,
        cpf: item.cpf,
        nome: item.nome
      };
    },
    onServidorResponsavelChange(item) {
      this.servidorResponsavel = this.$utils.Components.fromDropdownItem(
        item,
        "id",
        "content",
        true
      );

      if (!this.servidorResponsavel) {
        this.usuarioCollection.items = null;
        return;
      }

      delete this.servidorResponsavel.content;

      this.getPageUsuarios(0);
    },
    onKeydown() {
      this.getPageUsuarios(0);
    },
    onPageChange(event) {
      this.getPageUsuarios(event - 1);
    },
    resetBuscaUsuarios() {
      this.usuarioCollection.items = null;
      this.pagination.value = 1;
      this.pagination.numPages = 0;
    },
    searchServidorResponsavel(value) {
      return this.$http
        .get(`${API_URL}/core/pessoaFisica/busca?query=${value}`)
        .then(response =>
          (response.data || []).map(item =>
            this.mapServidorResponsavelToDropdownItem(item)
          )
        )
        .catch(error =>
          this.$notification.pushError(
            `Houve um erro ao buscar pelo servidor responsável: ${error.message}`
          )
        );
    }
  }
};
</script>

<style scoped>
.usuarios-list /deep/ h3 {
  font-size: 1.64rem;
  margin: 1.0933333333rem 0 0.656rem 0;
}
.usuarios-list /deep/ h4 {
  font-size: 1.15rem;
  margin: 0.7666666667rem 0 0.46rem 0;
}

.row {
  margin-bottom: 0;
}
</style>
